import type { ReactElement, ReactNode } from 'react';

/**
 * Empty and error states.
 *
 * An empty screen is an invitation to act, not a mood: it says what this list
 * is for and what to do next. An error says what happened and how to fix it. It
 * does not apologise and it is never vague.
 */

interface EmptyStateProps {
  readonly headline: string;
  readonly detail: string;
  readonly action?: ReactNode;
}

export function EmptyState({ headline, detail, action }: EmptyStateProps): ReactElement {
  return (
    <div className="px-4 py-10 text-center">
      {/* A single hairline rule stands in for the empty table's first row. */}
      <div className="mx-auto mb-5 h-px w-16 bg-rule-strong" />
      <p className="text-body font-semibold">{headline}</p>
      <p className="mx-auto mt-1.5 max-w-sm text-ink-muted">{detail}</p>
      {action !== undefined && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

interface ErrorStateProps {
  readonly headline: string;
  readonly detail: string;
  readonly onRetry?: () => void;
  readonly retryLabel?: string;
}

export function ErrorState({
  headline,
  detail,
  onRetry,
  retryLabel = 'Try again',
}: ErrorStateProps): ReactElement {
  return (
    <div className="border-l-[3px] border-l-alert bg-alert-wash px-4 py-4" role="alert">
      <p className="font-semibold text-alert">{headline}</p>
      <p className="mt-1 text-ink">{detail}</p>
      {onRetry !== undefined && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 h-8 rounded border border-alert/40 bg-panel px-3 font-medium text-alert hover:bg-panel/70"
        >
          {retryLabel}
        </button>
      )}
    </div>
  );
}
